import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { CandidateStatusEnum } from "../../types/candidate";
import {
  STATUS_CONFIG,
  STATUS_STYLE_MAP,
  STATUS_TRANSITIONS,
} from "../../types/candidate";
import { statusPillClasses } from "../ui/StatusPill";
import { cn } from "../../lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CandidateStatusDropdownProps {
  value: CandidateStatusEnum;
  onChange: (newStatus: CandidateStatusEnum) => void;
  /** If provided, only these statuses appear in the dropdown options. */
  allowedStatuses?: CandidateStatusEnum[];
  /** When true, disables the trigger button (e.g. while an update is in flight). */
  isUpdating?: boolean;
  /** "sm" = compact profile-card pill (v3 status badge); "md" = table cell (default). */
  size?: "sm" | "md";
}

const FALLBACK_STYLE = STATUS_STYLE_MAP.grey;

function getStyleForStatus(status: CandidateStatusEnum) {
  const config = STATUS_CONFIG[status];
  if (!config) return FALLBACK_STYLE;
  return STATUS_STYLE_MAP[config.color] ?? FALLBACK_STYLE;
}

// ---------------------------------------------------------------------------
// Chevron Icon
// ---------------------------------------------------------------------------

function ChevronDownIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      className="pointer-events-none"
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// CandidateStatusDropdown
// ---------------------------------------------------------------------------

export function CandidateStatusDropdown({
  value,
  onChange,
  allowedStatuses,
  isUpdating = false,
  size = "md",
}: CandidateStatusDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);

  // Fixed-position coordinates for the dropdown menu.
  // Using `position: fixed` allows the dropdown to escape all ancestor
  // `overflow: hidden / auto` clipping contexts while remaining in the
  // dialog's DOM tree (so it renders inside the top-layer).
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    openUpward: boolean;
  }>({ top: 0, left: 0, openUpward: false });

  const menuId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Resolve the list of statuses to show in the dropdown
  const options: CandidateStatusEnum[] = allowedStatuses
    ? allowedStatuses.filter((s) => s !== value)
    : (STATUS_TRANSITIONS[value] ?? []);

  const hasOptions = options.length > 0;

  // Current status config
  const currentConfig = STATUS_CONFIG[value];
  const currentLabel = currentConfig?.label ?? value;

  // ------- Position calculation -------

  /** Estimate how tall the dropdown menu will be (border + padding + items). */
  const MENU_ITEM_HEIGHT = 38; // ~py-1.5 + h-[30px] pill + gap
  const MENU_PADDING = 8; // p-1 (4px top + 4px bottom)
  const MENU_MARGIN = 6; // mt-1.5 (6px)
  const VIEWPORT_EDGE_MARGIN = 8;

  const computeMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const estimatedMenuHeight =
      options.length * MENU_ITEM_HEIGHT + MENU_PADDING + MENU_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < estimatedMenuHeight + VIEWPORT_EDGE_MARGIN;

    setMenuPos({
      top: openUpward ? rect.top : rect.bottom + MENU_MARGIN,
      left: rect.left,
      openUpward,
    });
  }, [options.length]);

  // ------- Close helpers -------

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Click-outside, ESC, and scroll handler
  useEffect(() => {
    if (!isOpen) return;

    function handleEvent(event: MouseEvent | KeyboardEvent) {
      if (event instanceof KeyboardEvent && event.key === "Escape") {
        closeMenu();
        return;
      }

      if (event instanceof MouseEvent) {
        const target = event.target as Node;
        if (
          menuRef.current &&
          !menuRef.current.contains(target) &&
          triggerRef.current &&
          !triggerRef.current.contains(target)
        ) {
          closeMenu();
        }
      }
    }

    // Close when any ancestor scrolls so the dropdown does not float
    // in a stale position.
    function handleScroll() {
      closeMenu();
    }

    document.addEventListener("mousedown", handleEvent);
    document.addEventListener("keydown", handleEvent);
    // Use capture phase to catch scroll on any ancestor (including the
    // overflow-auto table container) before it reaches the document.
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleEvent);
      document.removeEventListener("keydown", handleEvent);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, closeMenu]);

  // ------- Keyboard navigation -------

  const handleMenuKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const itemCount = options.length;
      let nextIndex = focusedIndex;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          nextIndex = (focusedIndex + 1) % itemCount;
          break;
        case "ArrowUp":
          event.preventDefault();
          nextIndex = (focusedIndex - 1 + itemCount) % itemCount;
          break;
        case "Home":
          event.preventDefault();
          nextIndex = 0;
          break;
        case "End":
          event.preventDefault();
          nextIndex = itemCount - 1;
          break;
        case "Tab":
          event.preventDefault();
          closeMenu();
          return;
        default:
          return;
      }

      setFocusedIndex(nextIndex);
      itemRefs.current[nextIndex]?.focus();
    },
    [focusedIndex, options.length]
  );

  // ------- Item selection -------

  const handleItemClick = useCallback(
    (status: CandidateStatusEnum) => {
      setIsOpen(false);
      triggerRef.current?.focus();
      onChange(status);
    },
    [onChange]
  );

  return (
    <div className="relative inline-flex">
      {/* Trigger pill */}
      <button
        ref={triggerRef}
        type="button"
        disabled={!hasOptions || isUpdating}
        onClick={() => {
          if (!isOpen) computeMenuPosition();
          setIsOpen((prev) => !prev);
          setFocusedIndex(0);
        }}
        aria-haspopup={hasOptions ? "menu" : undefined}
        aria-expanded={hasOptions ? isOpen : undefined}
        aria-controls={isOpen ? menuId : undefined}
        aria-label={`Status: ${currentLabel}`}
        className={cn(
          statusPillClasses(STATUS_CONFIG[value]?.color ?? "grey"),
          size === "sm"
            ? "h-7 gap-1.5 px-3 text-xs shadow-2xs"
            : "h-9 min-w-[136px] justify-between px-4 shadow-card",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2",
          isUpdating
            ? "cursor-not-allowed opacity-60"
            : hasOptions
              ? "cursor-pointer motion-safe:transition-opacity hover:opacity-80"
              : "cursor-default"
        )}
      >
        {currentLabel}
        {hasOptions && <ChevronDownIcon />}
      </button>

      {/* Dropdown menu — portaled to document.body so it physically escapes
          all overflow:hidden/auto ancestors in the regular page layout.
          position:fixed with coordinates from getBoundingClientRect() keeps
          it aligned to the trigger. The outside-click handler and menuRef
          work correctly because the ref still points to the same DOM node
          and document.addEventListener fires regardless of DOM position.
          Note: when used inside a <dialog> opened with showModal() the dialog
          is already in the browser's top-layer, so the portal is a no-op from
          a stacking perspective — fixed positioning is what matters there. */}
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Status options"
            onKeyDown={handleMenuKeyDown}
            style={{
              position: "fixed",
              top: menuPos.openUpward ? "auto" : menuPos.top,
              bottom: menuPos.openUpward
                ? window.innerHeight - menuPos.top + MENU_MARGIN
                : "auto",
              left: menuPos.left,
            }}
            className="z-50 flex min-w-[180px] flex-col gap-0.5 rounded-2xl border border-black/[0.06] bg-white p-1.5 shadow-pop motion-safe:animate-dropdown-slide-in"
          >
            {options.map((status, index) => {
              const config = STATUS_CONFIG[status];
              const label = config?.label ?? status;
              const style = getStyleForStatus(status);

              return (
                <button
                  key={status}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                    if (index === 0 && el) el.focus();
                  }}
                  type="button"
                  role="menuitem"
                  tabIndex={focusedIndex === index ? 0 : -1}
                  onClick={() => handleItemClick(status)}
                  className={`flex w-full cursor-pointer items-center rounded-xl px-3 py-2 text-[13px] font-semibold hover:bg-black/[0.04] motion-safe:transition-colors focus-visible:outline-none focus-visible:bg-black/[0.04] ${style.text}`}
                >
                  {label}
                </button>
              );
            })}
          </div>,
          // A showModal() dialog makes the rest of the document inert, so the
          // menu must render inside the dialog's subtree to stay clickable.
          triggerRef.current?.closest("dialog") ?? document.body
        )}
    </div>
  );
}
